<template>
  <div class="patient-dashboard container bg-medium-gray pb-1" :class="readyClass">
    
    <!-- header -->
    <div class="container flex">
    
      <div class="flex-grow px-1 py-1 fs-lg">
        <b>CONCERN</b> Dashboard
      </div>
    
      <div
        class="flex-shrink px-1 py-1 fs-lg border-radius-tl c-almost-white"
        :class="concernColor"
        v-if="concernIsNotZero"
      >
        The patient is at <b>{{concernLevelText}} risk</b> for decline.
      </div>
    
    </div>
    
    <!-- demographics -->
    <div class="flex flex-start flex-center bg-dark-gray">
      
      <div class="px-1 py-1">
        
        <div
          class="border-radius px-1 py-1 fs-lg c-almost-white text-center"
          :class="concernColor"
          v-if="concernIsNotZero"
        >
          <b>{{concernLevelText}}</b>
        </div>

      </div>
      <div class="px-1 py-1 fs-lg c-almost-white">
        {{demographics.firstName}} {{demographics.lastName}}
      </div>
      
      <div class="px-1 py-1 c-almost-white">
        <div class="fs-sm c-medium-gray">
          Unit/Bed
        </div>
        <div class="pt-1_2">
          {{location}}
        </div>
      </div>
      
      <div class="separator bg-medium-gray height-2"></div>
      
      <div class="px-1 py-1 c-almost-white">
        <div class="fs-sm c-medium-gray">
          Date of Birth
        </div>
        <div class="pt-1_2">
          {{displayBirthdate}}
        </div>
      </div>
      
      <div class="separator bg-medium-gray height-2"></div>
      
      <div class="px-1 py-1 c-almost-white">
        <div class="fs-sm c-medium-gray">
          MRN
        </div>
        <div class="pt-1_2">
          {{demographics.mrn}}
        </div>
      </div>
    </div>

    <div class="container px-1 py-1 flex flex-align-start">

      <!-- links -->      
      <div class="bg-almost-white px-1 py-1 flex-shrink width-15 box-shadow">
        
        <div class="strong">About CONCERN</div>
        
        <div class="fs-sm mt-1">
          The CONCERN algorithm predicts patient decline based on nursing documentation.
        </div>
        
        <ul class="mt-1_2">
        
          <li class="b bt bc-medium-gray">
            <a
              class="py-1_2"
              href="#concern-model"
              @click="log( { eventType: 'clicked link', eventDetails: 'View the Model' } )"
            >
              View the Model
            </a>
          </li>

          <li class="b bt bc-medium-gray">
            <a
              class="py-1_2"
              href="video/video.html"
              @click="log( { eventType: 'clicked link', eventDetails: 'Watch the Video' } )"
            >
              Watch the Video
            </a>
          </li>

          <li class="b bt bc-medium-gray">
            <a
              class="py-1_2"
              href="research/research.html"
              @click="log( { eventType: 'clicked link', eventDetails: 'Review the Research' } )"
            >
              Review the Research
            </a>
          </li>

          <li class="b bt bc-medium-gray">
            <a
              class="py-1_2"
              href="faq/faq.html"
              @click="log( { eventType: 'clicked link', eventDetails: 'FAQs' } )"
            >
              FAQs
            </a>
          </li>

          <li class="b bt bc-medium-gray">
            <a
              class="py-1_2"
              href="#"
              @click="log( { eventType: 'clicked link', eventDetails: 'Contact Us' } );contactFormVisible=true"
            >
              Contact Us
            </a>
          </li>

        </ul>
      </div>

      <div class="
        flex-grow
        ml-1 mr-1
        px-1 py-1
        box-shadow
        bg-almost-white
      ">
        <concern-factors
          :factors="concernData"
        />
      </div>
      
      <div class="box-shadow bg-almost-white px-1 py-1 width-30">
        <div class="strong mb-1">CONCERN Trend</div>
        <concern-trend
          :concernScores="concernScores"
          :concernValuesWithColors="concernValuesWithColors"
        />
      </div>

    </div>
    
    <div class="container">
      <div class="box-shadow bg-almost-white px-1 py-1 ml-17 mr-1">
        <div class="strong mb-1">CONCERN Model</div>
        <concern-model
          :greenYellowCutoff="greenYellowCutoff"
          :yellowRedCutoff="yellowRedCutoff"
          :patientPercentile="patientPercentile"
        />
      </div>
    </div>

    <contact-us
      :visible="contactFormVisible"
      @close="contactFormVisible = false;"
    />
  </div>

</template>

<script>
import { mapGetters } from 'vuex';
import { doLogging }  from '../modules/log';

import ConcernFactors from './ConcernFactors.vue';
import ConcernTrend   from './ConcernTrend.vue';
import ConcernModel   from './ConcernModel.vue';
import ContactUs      from './ContactUs.vue';

export default {

  name: 'PatientDashboard',
  
  components: {
    ConcernFactors,
    ConcernTrend,
    ConcernModel,
    ContactUs,
  },

  props: {
  },
  
  data () {
    return {
      factors: [],
      userActivity: false,
      contactFormVisible: false,
    };
  },

  computed: {
    
    ...mapGetters([
      'demographics',
      'concernData',
      'concernNoteData',
      'greenYellowCutoff',
      'yellowRedCutoff',
      'patientPercentile',
      'loggingContext',
      'location',
      'concernIsZero',
      'loggingContext',
    ]),

    concernIsNotZero () {
      return !( this.concernIsZero );
    },

    concernColor () {
      return {
        'high'  : 'bg-red',
        'medium': 'bg-yellow',
        'low'   : 'bg-green'
      }[ this.concernLevelText ];
    },

    displayBirthdate () {
      const iso = this.demographics.birthdateISO;
      if ( iso ) {
        return iso.substring( 5, 7 ) + '-'
             + iso.substring( 8, 10 ) + '-'
             + iso.substring( 0, 4 );
      } else { return ''; }
    },

    concernScores () {
      return this.concernData.map( d => ({
        dtm: d.dtm,
        score: { value: d.value.concern_score, color: d.value.concern_score_color }
      })) || [];
    },

    concernValues () {
      return this.concernScores.map( s => s.score.value );
    },

    concernColors () {
      return this.concernScores.map( s => s.score.color );
    },

    concernValuesWithColors () {
      
      const colors = this.concernColors;

      return this.concernScores.map( ( v, i ) => ({
        dtm: this.isoDatetoJS( v.dtm ),
        // dtm: this.isoDatetoJS( v.CreatedAt ),
        value: parseInt( v.score.value, 10 ),
        valueText: '' + v.score.value,
        color: colors[ i ]
      }));
    },

    concernLevelText () {
      const first = this.concernValuesWithColors && this.concernValuesWithColors[0] || null;
      // const [ last ] = this.concernValuesWithColors.slice( -1 );
      const map = {
        'red':    'high',
        'yellow': 'medium',
        'green':  'low'
      };

      return first && first.color && map[ first.color ] || '';
      // return last && last.color && map[ last.color ] || '';
    },
  },

  watch: {
    loggingContext () {
      this.log({ eventType: 'launch', eventDetails: '' });
    }
  },

  methods: {

    log ( event ) {
      event = {
        ...event,
        ...this.loggingContext
      };      
      doLogging( event );
    },

    isoDatetoJS ( isoDtm ) {

      const year = parseInt( isoDtm.substring( 0, 4 ), 10 );
      const month = parseInt( isoDtm.substring( 5, 7 ), 10 ) - 1;
      const date = parseInt( isoDtm.substring( 8, 10 ), 10 );
      const hours = parseInt( isoDtm.substring( 11, 13 ), 10 );
      const minutes = parseInt( isoDtm.substring( 14, 16 ), 10 );
      const seconds = parseInt( isoDtm.substring( 17, 19 ), 10 );

      return new Date( year, month, date, hours, minutes, seconds );
    },
  },

  mounted () {

    this.$store.dispatch( 'getPatientData' );
    // this.log( { eventType: 'launch', eventDetails: '' } );

    //  log user activity
    document.addEventListener( 'mousemove', () => {
      this.userActivity = true;
    });

    setInterval( () => {
      if ( this.userActivity ) {
        this.log( { eventType: 'user activity', eventDetails: '' } );
      }
      this.userActivity = false;
    }, 60 * 1000 );

  }
}
</script>

<style lang="scss">
.patient-dashboard { display:none; }
.patient-dashboard.app-ready { display:block; }

a {
  display: block;
  text-decoration: none;
  color: inherit;

  &:hover {
    text-decoration: underline;
  }
}
</style>
